import { useEffect, useRef } from 'react';
import { useCatalog } from '../hooks/useCatalog';
import { useWizardCompletion } from '../hooks/useWizardCompletion';
import { calculateAllTabsCompletion } from '../utils/wizardCompletionCalculator';
import { logger } from '../utils/logger';

interface PreloadedCatalogItem {
  images?: unknown[];
  coverImageId?: string | null;
}

interface EditModeCompletionInitializerProps {
  editId: string;
  ownerId?: string | null;
  /**
   * Optional preloaded catalog item data from the parent (UploadObject).
   * When provided, skips the getCatalogItem API call to avoid a duplicate
   * network request. Fixes #1059.
   */
  preloadedCatalogItem?: PreloadedCatalogItem | null;
}

/**
 * Fetches all tab data in parallel for an existing catalog item and
 * computes completion state upfront via bulkInitialize.
 *
 * Must be rendered INSIDE WizardCompletionProvider (which is why it's
 * a separate component - UploadObject sits above the provider).
 */
export const EditModeCompletionInitializer = ({ editId, ownerId, preloadedCatalogItem }: EditModeCompletionInitializerProps) => {
  const { getCatalogItem, getDescription, getMeasurements, getKeywords, getExhibitions, getProvenanceChain, getAdditionalInfo } = useCatalog();
  const { bulkInitialize } = useWizardCompletion();
  const initializedRef = useRef<string | null>(null);

  useEffect(() => {
    if (!editId || initializedRef.current === editId) return;
    // When a preloadedCatalogItem prop is provided (even if it's an empty fallback),
    // wait for it to be non-null before initializing. This ensures we always use the
    // preloaded data rather than making a second getCatalogItem request. Fixes #1059.
    if (preloadedCatalogItem === undefined) {
      // undefined means UploadObject hasn't passed the prop at all — no preloading
      // is in use, so fall through and fetch ourselves.
    } else if (preloadedCatalogItem === null) {
      // null means preloading is enabled but the fetch hasn't completed yet — wait.
      return;
    }
    initializedRef.current = editId;

    const fetchAndInitialize = async () => {
      try {
        // Use preloaded catalog item when available to avoid a duplicate API call.
        // UploadObject fetches getCatalogItem separately to load images into upload
        // context; reusing that result here eliminates the redundant network request.
        const catalogItemPromise = preloadedCatalogItem != null
          ? Promise.resolve(preloadedCatalogItem)
          : getCatalogItem(editId, ownerId).catch(() => ({ images: [], coverImageId: null }));

        const [catalogItem, description, measurements, keywords, exhibitionsResult, provenanceChain, additionalInfo] = await Promise.all([
          catalogItemPromise,
          getDescription(editId, ownerId).catch(() => ({})),
          getMeasurements(editId, ownerId).catch(() => ({})),
          getKeywords(editId, ownerId).catch(() => ({})),
          getExhibitions(editId, ownerId).catch(() => ({ exhibitions: [], stepNotes: {} })),
          getProvenanceChain(editId, ownerId).catch(() => ({ records: [], version: 0, chainLength: 0 })),
          getAdditionalInfo(editId, ownerId).catch(() => ({}))
        ]);

        const descriptionData = description as Record<string, unknown>;
        const tabsCompletion = calculateAllTabsCompletion({
          upload: {
            images: (catalogItem as { images?: unknown[] }).images || [],
            coverImageId: (catalogItem as { coverImageId?: string | null }).coverImageId || null
          },
          describe: descriptionData,
          media: {
            workType: descriptionData.workType as Record<string, unknown> | null | undefined,
            techniques: descriptionData.techniques as unknown[] | undefined,
            materials: descriptionData.materials as unknown[] | undefined,
            substrate: descriptionData.substrate as unknown[] | undefined
          },
          measure: measurements as Record<string, unknown>,
          catalogKeywords: keywords as Record<string, unknown>,
          provenance: {
            provenanceRecords: ((provenanceChain as { records?: Array<{ publicRendering: string; internalRendering: string }> })?.records ?? []) as Array<{ publicRendering: string; internalRendering: string }>,
            exhibitions: (exhibitionsResult as { exhibitions: unknown[] }).exhibitions as Array<{ type: string; exhibitionType?: string; link?: string; publicationText?: string }>
          },
          additionalInfo: additionalInfo as Record<string, unknown>
        });

        bulkInitialize(tabsCompletion);
      } catch (error) {
        // Silently fail - tabs will compute their own completion when mounted
        logger.warn('EditModeCompletionInitializer: Failed to bulk initialize', error);
      }
    };

    fetchAndInitialize();
  }, [editId, ownerId, preloadedCatalogItem, getCatalogItem, getDescription, getMeasurements, getKeywords, getExhibitions, getProvenanceChain, getAdditionalInfo, bulkInitialize]);

  return null;
};
